import React, { useState } from "react";
import { Outlet, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Search, ShoppingCart, User, LogOut, Store, Shield, Menu, X, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

const FCLogo = () => (
  <svg width="36" height="36" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" aria-label="Florvanta">
    <text x="8" y="72" fontFamily="Clash Display, Satoshi, sans-serif" fontSize="72" fontWeight="700" fill="#0F3D2E">F</text>
    <path d="M60 18 A32 32 0 1 0 60 82" stroke="#14A085" strokeWidth="12" fill="none" strokeLinecap="round" />
  </svg>
);

export default function Layout() {
  const { user, cartCount, logout } = useAuth();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);

  const submitSearch = (e) => {
    e.preventDefault();
    nav(`/products?q=${encodeURIComponent(q)}`);
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#F9F9F7]">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-[#E2E8E5]" data-testid="app-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="h-16 flex items-center justify-between gap-4">
            <Link to="/" className="flex items-center gap-2" data-testid="brand-link">
              <FCLogo />
              <div className="leading-none">
                <div className="font-display text-lg font-bold text-[#0F3D2E]">FLORVANTA</div>
                <div className="text-[10px] tracking-[0.25em] text-[#14A085]">COMMERCE</div>
              </div>
            </Link>

            <form onSubmit={submitSearch} className="hidden md:flex flex-1 max-w-xl mx-6" data-testid="search-form">
              <div className="flex-1 flex items-center gap-2 rounded-full border border-[#E2E8E5] bg-white px-4 h-11 focus-within:ring-2 focus-within:ring-[#14A085]">
                <Search size={18} className="text-[#6E877C]" />
                <input
                  data-testid="search-input"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  className="bg-transparent flex-1 outline-none text-sm text-[#0F3D2E] placeholder:text-[#6E877C]"
                  placeholder="Search clothing, electronics, groceries…"
                />
              </div>
            </form>

            <nav className="hidden md:flex items-center gap-1">
              <Link to="/sell" className="px-3 py-2 text-sm text-[#0F3D2E] hover:text-[#14A085]" data-testid="nav-sell">
                <Store size={16} className="inline mr-1" /> Sell
              </Link>
              {user && user.role === "admin" && (
                <Link to="/admin" className="px-3 py-2 text-sm text-[#0F3D2E] hover:text-[#14A085]" data-testid="nav-admin">
                  <Shield size={16} className="inline mr-1" /> Admin
                </Link>
              )}
              {user && user.role === "vendor" && (
                <Link to="/vendor" className="px-3 py-2 text-sm text-[#0F3D2E] hover:text-[#14A085]" data-testid="nav-vendor">
                  Vendor
                </Link>
              )}
              <Link to="/cart" className="relative px-3 py-2" data-testid="nav-cart">
                <ShoppingCart size={20} className="text-[#0F3D2E]" />
                {cartCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-[#14A085] text-white text-[10px] px-1.5 rounded-full" data-testid="cart-count">
                    {cartCount}
                  </span>
                )}
              </Link>
              {user ? (
                <div className="flex items-center gap-2 ml-2">
                  <Link to="/orders" className="px-3 py-2 text-sm text-[#0F3D2E] hover:text-[#14A085]" data-testid="nav-orders">
                    <User size={16} className="inline mr-1" />
                    {user.name?.split(" ")[0] || "Account"}
                  </Link>
                  <Button variant="ghost" onClick={logout} size="sm" data-testid="logout-btn" className="text-[#0F3D2E]">
                    <LogOut size={16} />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2 ml-2">
                  <Link to="/login" data-testid="nav-login">
                    <Button variant="ghost" size="sm" className="text-[#0F3D2E]">Login</Button>
                  </Link>
                  <Link to="/register" data-testid="nav-register">
                    <Button size="sm" className="bg-[#0F3D2E] hover:bg-[#14A085] text-white rounded-full">
                      Sign up
                    </Button>
                  </Link>
                </div>
              )}
            </nav>

            <button className="md:hidden p-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn">
              {open ? <X /> : <Menu />}
            </button>
          </div>

          {open && (
            <div className="md:hidden pb-4 space-y-2" data-testid="mobile-menu">
              <form onSubmit={submitSearch} className="flex items-center gap-2 rounded-full border border-[#E2E8E5] bg-white px-4 h-11">
                <Search size={18} className="text-[#6E877C]" />
                <input value={q} onChange={(e) => setQ(e.target.value)} className="bg-transparent flex-1 outline-none text-sm" placeholder="Search…" data-testid="mobile-search-input" />
              </form>
              <Link to="/products?category=clothing" className="block py-2 text-sm">Clothing</Link>
              <Link to="/products?category=electronics" className="block py-2 text-sm">Electronics</Link>
              <Link to="/products?category=grocery" className="block py-2 text-sm">Grocery</Link>
              <Link to="/cart" className="block py-2 text-sm">Cart ({cartCount})</Link>
              <Link to="/sell" className="block py-2 text-sm">Sell on Florvanta</Link>
              {user ? (
                <>
                  <Link to="/orders" className="block py-2 text-sm">My Orders</Link>
                  {user.role === "vendor" && <Link to="/vendor" className="block py-2 text-sm">Vendor Panel</Link>}
                  {user.role === "admin" && <Link to="/admin" className="block py-2 text-sm">Admin Panel</Link>}
                  <button onClick={logout} className="block py-2 text-sm text-red-600">Logout</button>
                </>
              ) : (
                <>
                  <Link to="/login" className="block py-2 text-sm">Login</Link>
                  <Link to="/register" className="block py-2 text-sm">Sign Up</Link>
                </>
              )}
            </div>
          )}
        </div>
        <div className="hidden md:block border-t border-[#E2E8E5] bg-white/60">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-10 flex items-center gap-6 text-sm">
            <Link to="/products" className="text-[#3B5249] hover:text-[#14A085]" data-testid="cat-all">All</Link>
            <Link to="/products?category=clothing" className="text-[#3B5249] hover:text-[#14A085]" data-testid="cat-clothing">Clothing</Link>
            <Link to="/products?category=electronics" className="text-[#3B5249] hover:text-[#14A085]" data-testid="cat-electronics">Electronics</Link>
            <Link to="/products?category=grocery" className="text-[#3B5249] hover:text-[#14A085]" data-testid="cat-grocery">Grocery</Link>
            <span className="ml-auto text-xs text-[#6E877C]">PAN-India · Only 1% commission for sellers</span>
          </div>
        </div>
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      {/* Floating WhatsApp chat button */}
      <a
        href="https://wa.me/919147425375?text=Hi%20Florvanta%2C%20I%20have%20a%20question."
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 h-14 w-14 rounded-full bg-[#25D366] hover:bg-[#128C7E] shadow-lg flex items-center justify-center transition-colors group"
        aria-label="Chat on WhatsApp"
        data-testid="whatsapp-fab"
      >
        <MessageCircle className="text-white" size={26} />
        <span className="pointer-events-none absolute right-16 whitespace-nowrap rounded-full bg-[#0F3D2E] text-white text-xs px-3 py-1 opacity-0 group-hover:opacity-100 transition-opacity">
          Chat on WhatsApp
        </span>
      </a>

      <footer className="mt-24 border-t border-[#E2E8E5] bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid md:grid-cols-4 gap-8">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <FCLogo />
              <div>
                <div className="font-display text-lg font-bold text-[#0F3D2E]">FLORVANTA</div>
                <div className="text-[10px] tracking-[0.25em] text-[#14A085]">COMMERCE PVT LTD</div>
              </div>
            </div>
            <p className="text-sm text-[#3B5249]">India's fairest multi-vendor marketplace. Sellers keep 99% — we take just 1%.</p>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#14A085] mb-3">Shop</div>
            <ul className="space-y-2 text-sm text-[#3B5249]">
              <li><Link to="/products?category=clothing" className="hover:text-[#14A085]">Clothing</Link></li>
              <li><Link to="/products?category=electronics" className="hover:text-[#14A085]">Electronics</Link></li>
              <li><Link to="/products?category=grocery" className="hover:text-[#14A085]">Grocery</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#14A085] mb-3">Sell</div>
            <ul className="space-y-2 text-sm text-[#3B5249]">
              <li><Link to="/sell" className="hover:text-[#14A085]">Become a Seller</Link></li>
              <li><Link to="/vendor" className="hover:text-[#14A085]">Vendor Dashboard</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#14A085] mb-3">Help &amp; Company</div>
            <ul className="space-y-2 text-sm text-[#3B5249]">
              <li><Link to="/contact" className="hover:text-[#14A085]" data-testid="footer-contact">Contact Us</Link></li>
              <li><Link to="/faqs" className="hover:text-[#14A085]" data-testid="footer-faqs">FAQs</Link></li>
              <li><Link to="/shipping-policy" className="hover:text-[#14A085]" data-testid="footer-shipping">Shipping Policy</Link></li>
              <li><Link to="/return-policy" className="hover:text-[#14A085]" data-testid="footer-returns">Return Policy</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-[#E2E8E5]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col md:flex-row gap-3 md:items-center md:justify-between text-xs text-[#6E877C]">
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              <Link to="/terms" className="hover:text-[#14A085]" data-testid="footer-terms">Terms &amp; Conditions</Link>
              <Link to="/privacy" className="hover:text-[#14A085]" data-testid="footer-privacy">Privacy Policy</Link>
              <Link to="/refund-policy" className="hover:text-[#14A085]" data-testid="footer-refund">Refund Policy</Link>
              <Link to="/shipping-policy" className="hover:text-[#14A085]">Shipping</Link>
              <Link to="/return-policy" className="hover:text-[#14A085]">Returns</Link>
              <Link to="/faqs" className="hover:text-[#14A085]">FAQs</Link>
              <Link to="/contact" className="hover:text-[#14A085]">Contact</Link>
            </div>
            <span>© {new Date().getFullYear()} Florvanta Commerce Pvt Ltd. Made in Bharat.</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
